'use client';

import { useState, useCallback, useMemo } from 'react';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import type { DocumentLineItem } from '@/hooks/useDocumentsWorkItems';
import type { ExpenseAccountOption } from '@/lib/finance/expenseAccountMapping';
import type { DocumentTaxRate } from '@/components/finance/modals/SortableLineItem';
import { useAllocationDimensions, type LineItemAllocation } from '@/hooks/useAllocationDimensions';
import type { SplitAllocation } from '@/components/shared/finance/SplitAllocationEditor';

export interface AiSuggestion {
  account_code: string;
  account_name: string;
  confidence: number;
}

export interface UseExpenseLineItemsOptions {
  expenseLineItems: DocumentLineItem[];
  setExpenseLineItems: React.Dispatch<React.SetStateAction<DocumentLineItem[]>>;
  expenseAccounts: ExpenseAccountOption[];
  vendorName?: string;
  totalAmount?: number;
  taxRates?: DocumentTaxRate[];
}

export interface UseExpenseLineItemsReturn {
  // AI suggestions
  aiLoading: boolean;
  aiSuggestions: Record<number, AiSuggestion>;
  aiApplied: boolean;
  rejectedSuggestions: Set<number>;
  handleAiSuggestGl: () => Promise<void>;
  handleAcceptSuggestion: (idx: number) => void;
  handleRejectSuggestion: (idx: number) => void;
  handleAcceptAll: () => void;
  handleRejectAll: () => void;

  // Allocation helpers
  allocationDimensions: ReturnType<typeof useAllocationDimensions>['dimensions'];
  getLineItemDimensions: (idx: number) => LineItemAllocation[];
  getLineItemSplits: (idx: number) => SplitAllocation[];
  updateLineItemAllocations: (idx: number, allocations: { dimensions?: LineItemAllocation[]; splits?: SplitAllocation[] }) => void;
  expandedAllocations: Set<number>;
  toggleExpandedAllocations: (idx: number) => void;

  // Line item CRUD
  handleAddLineItem: (newItem: { description: string; quantity: number; unit_price: number; tax_rate_id: number | undefined }) => boolean;
  handleRemoveLineItem: (idx: number) => void;
  handleUpdateLineItem: (idx: number, field: keyof DocumentLineItem, value: string | number) => void;
  handleUpdateTaxRate: (idx: number, taxRateId: number | undefined, taxRates: DocumentTaxRate[]) => void;

  // Computed
  computedTotal: number;
}

export function useExpenseLineItems({
  expenseLineItems,
  setExpenseLineItems,
  expenseAccounts,
  vendorName,
  totalAmount,
  taxRates = [],
}: UseExpenseLineItemsOptions): UseExpenseLineItemsReturn {
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<Record<number, AiSuggestion>>({});
  const [aiApplied, setAiApplied] = useState(false);
  const [rejectedSuggestions, setRejectedSuggestions] = useState<Set<number>>(new Set());
  const [expandedAllocations, setExpandedAllocations] = useState<Set<number>>(new Set());
  const { dimensions: allocationDimensions } = useAllocationDimensions();

  // --- Allocation helpers ---

  const getLineItemDimensions = useCallback((idx: number): LineItemAllocation[] => {
    return expenseLineItems[idx]?.allocations?.dimensions ?? [];
  }, [expenseLineItems]);

  const getLineItemSplits = useCallback((idx: number): SplitAllocation[] => {
    return expenseLineItems[idx]?.allocations?.splits ?? [];
  }, [expenseLineItems]);

  const updateLineItemAllocations = useCallback((idx: number, allocations: { dimensions?: LineItemAllocation[]; splits?: SplitAllocation[] }) => {
    setExpenseLineItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const current = item.allocations ?? {};
      const next = { ...current };
      if (allocations.dimensions !== undefined) next.dimensions = allocations.dimensions;
      if (allocations.splits !== undefined) next.splits = allocations.splits;
      const hasDimensions = next.dimensions && next.dimensions.length > 0;
      const hasSplits = next.splits && next.splits.length > 0;
      return { ...item, allocations: (hasDimensions || hasSplits) ? next : null };
    }));
  }, [setExpenseLineItems]);

  const toggleExpandedAllocations = useCallback((idx: number) => {
    setExpandedAllocations(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  }, []);

  // --- AI GL suggestions ---

  const handleAiSuggestGl = useCallback(async () => {
    if (expenseLineItems.length === 0) return;
    setAiLoading(true);
    setAiApplied(false);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        notify.error('Not authenticated');
        return;
      }
      const computedTotalVal = expenseLineItems.reduce((sum, item) => {
        const qty = Number(item.quantity) || 0;
        const price = Number(item.unit_price) || 0;
        const lineTotal = item.line_total != null ? Number(item.line_total) : (qty * price);
        return sum + lineTotal;
      }, 0);

      const response = await fetch('/api/v1/finance/ai-suggestions', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vendor_name: vendorName || null,
          total_amount: totalAmount ?? computedTotalVal,
          line_items: expenseLineItems.map((li) => ({
            description: li.description,
            quantity: Number(li.quantity) || 1,
            unit_price: Number(li.unit_price) || 0,
            total: li.line_total != null ? Number(li.line_total) : (Number(li.quantity) || 1) * (Number(li.unit_price) || 0),
          })),
          per_line_categorization: true,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        notify.error(err?.error || 'AI suggestion failed');
        return;
      }
      const data = await response.json();
      const perLine = data?.perLineCategorization as Array<{ index: number; account_code: string; account_name: string; confidence: number }> | undefined;
      if (perLine && perLine.length > 0) {
        const suggestionMap: Record<number, AiSuggestion> = {};
        for (const s of perLine) {
          if (s.account_code && s.confidence >= 60) suggestionMap[s.index] = { account_code: s.account_code, account_name: s.account_name, confidence: s.confidence };
        }
        setAiSuggestions(suggestionMap);
        setRejectedSuggestions(new Set());
        // Auto-apply only high-confidence suggestions (>=85%)
        const highConfidenceIndices = Object.entries(suggestionMap)
          .filter(([, s]) => s.confidence >= 85)
          .map(([idx]) => Number(idx));
        if (highConfidenceIndices.length > 0) {
          setExpenseLineItems(expenseLineItems.map((item, idx) => {
            if (highConfidenceIndices.includes(idx)) {
              return { ...item, expense_account_code: suggestionMap[idx].account_code };
            }
            return item;
          }));
        }
        setAiApplied(true);
        const count = Object.keys(suggestionMap).length;
        const autoCount = highConfidenceIndices.length;
        notify.success(`AI suggested GL codes for ${count} line item${count !== 1 ? 's' : ''}${autoCount > 0 ? ` (${autoCount} auto-applied)` : ''}`);
      } else {
        notify.info('AI could not suggest GL codes for these items');
      }
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'AI suggestion failed');
    } finally {
      setAiLoading(false);
    }
  }, [expenseLineItems, vendorName, totalAmount, setExpenseLineItems]);

  const handleAcceptSuggestion = useCallback((idx: number) => {
    setAiSuggestions(prev => {
      const suggestion = prev[idx];
      if (!suggestion) return prev;
      setExpenseLineItems(items => items.map((item, i) =>
        i === idx ? { ...item, expense_account_code: suggestion.account_code } : item,
      ));
      return prev;
    });
    setRejectedSuggestions(prev => { const next = new Set(prev); next.delete(idx); return next; });
  }, [setExpenseLineItems]);

  const handleRejectSuggestion = useCallback((idx: number) => {
    setRejectedSuggestions(prev => new Set(prev).add(idx));
  }, []);

  const handleAcceptAll = useCallback(() => {
    setExpenseLineItems(prev => prev.map((item, idx) => {
      const suggestion = aiSuggestions[idx];
      if (suggestion && suggestion.confidence >= 60 && !rejectedSuggestions.has(idx)) {
        return { ...item, expense_account_code: suggestion.account_code };
      }
      return item;
    }));
    setAiApplied(true);
  }, [aiSuggestions, rejectedSuggestions, setExpenseLineItems]);

  const handleRejectAll = useCallback(() => {
    setRejectedSuggestions(new Set(Object.keys(aiSuggestions).map(Number)));
  }, [aiSuggestions]);

  // --- Line item CRUD ---

  const handleAddLineItem = useCallback((newItem: { description: string; quantity: number; unit_price: number; tax_rate_id: number | undefined }): boolean => {
    if (!newItem.description.trim()) return false;
    const taxRate = taxRates.find(r => r.id === newItem.tax_rate_id);
    const taxRateValue = taxRate ? Number(taxRate.rate) : 15;
    const qty = Number(newItem.quantity) || 1;
    const price = Number(newItem.unit_price) || 0;
    const lineSubtotal = qty * price;
    const taxAmount = Math.round(lineSubtotal * (taxRateValue / 100) * 100) / 100;
    const newDocLineItem: DocumentLineItem = {
      description: newItem.description.trim(),
      quantity: qty,
      unit_price: price,
      unit: 'each',
      tax_rate: taxRateValue,
      tax_rate_id: newItem.tax_rate_id,
      tax_amount: taxAmount,
      line_total: Math.round((lineSubtotal + taxAmount) * 100) / 100,
      line_type: 'item',
      sort_order: expenseLineItems.length + 1,
    };
    setExpenseLineItems([...expenseLineItems, newDocLineItem]);
    return true;
  }, [expenseLineItems, taxRates, setExpenseLineItems]);

  const handleRemoveLineItem = useCallback((idx: number) => {
    setExpenseLineItems(prev => prev.filter((_, i) => i !== idx));
  }, [setExpenseLineItems]);

  const handleUpdateLineItem = useCallback((idx: number, field: keyof DocumentLineItem, value: string | number) => {
    setExpenseLineItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      if (field === 'quantity' || field === 'unit_price') {
        const qty = Number(updated.quantity) || 0;
        const price = Number(updated.unit_price) || 0;
        const subtotal = qty * price;
        const taxRate = Number(updated.tax_rate) || 0;
        updated.tax_amount = Math.round(subtotal * (taxRate / 100) * 100) / 100;
        updated.line_total = Math.round((subtotal + updated.tax_amount) * 100) / 100;
      }
      return updated;
    }));
  }, [setExpenseLineItems]);

  const handleUpdateTaxRate = useCallback((idx: number, taxRateId: number | undefined, allTaxRates: DocumentTaxRate[]) => {
    setExpenseLineItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const taxRate = allTaxRates.find(r => r.id === taxRateId);
      const newTaxRate = taxRate ? Number(taxRate.rate) : (item.tax_rate != null ? item.tax_rate : 15);
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unit_price) || 0;
      const subtotal = qty * price;
      const newTaxAmount = Math.round(subtotal * (newTaxRate / 100) * 100) / 100;
      return {
        ...item,
        tax_rate_id: taxRateId,
        tax_rate: newTaxRate,
        tax_amount: newTaxAmount,
        line_total: Math.round((subtotal + newTaxAmount) * 100) / 100,
      };
    }));
  }, [setExpenseLineItems]);

  // --- Computed ---

  const computedTotal = useMemo(() => {
    return expenseLineItems.reduce((sum, item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unit_price) || 0;
      const lineTotal = item.line_total != null ? Number(item.line_total) : (qty * price);
      return sum + lineTotal;
    }, 0);
  }, [expenseLineItems]);

  return {
    // AI suggestions
    aiLoading,
    aiSuggestions,
    aiApplied,
    rejectedSuggestions,
    handleAiSuggestGl,
    handleAcceptSuggestion,
    handleRejectSuggestion,
    handleAcceptAll,
    handleRejectAll,

    // Allocation helpers
    allocationDimensions,
    getLineItemDimensions,
    getLineItemSplits,
    updateLineItemAllocations,
    expandedAllocations,
    toggleExpandedAllocations,

    // Line item CRUD
    handleAddLineItem,
    handleRemoveLineItem,
    handleUpdateLineItem,
    handleUpdateTaxRate,

    // Computed
    computedTotal,
  };
}
